import React, { createContext, useContext, useState } from 'react';
import { defaultState, useExalusLocalStorage } from '../hooks/storage';
import { ChildrenProps } from '../../../types';

export type StateType = {
  isDashboardGridView: boolean;
  toggleGridView: () => void;
  activeGroupIndex: number;
  setActiveGroup: (activeGroupIndex: number) => void;
  activeChannelTabIndex: number;
  setActiveChannelTab: (activeChannelTabIndex: number) => void;
  activeDeviceTabIndex: number;
  setActiveDeviceTabIndex: (activeDeviceTabIndex: number) => void;
  isChannelsListView: boolean;
  toggleChannelsListView: () => void;
  readingChat: boolean;
  toggleReadingChat: () => void;
  getStorageData: () => void;
};

const ExalusStorageContext = createContext<StateType | undefined>(undefined);

export const useExalusStorageContext = (): StateType => useContext(ExalusStorageContext) as StateType;

export const ExalusStorageContextProvider: React.FC<ChildrenProps> = ({ children }) => {
  const { saveExalusDataStorage, readExalusDataStorage } = useExalusLocalStorage();
  const [isDashboardGridView, setIsDashboardGridView] = useState<boolean>(defaultState.isDashboardGridView);
  const [isChannelsListView, setIsChannelsListView] = useState<boolean>(defaultState.isChannelsListView);
  const [activeGroupIndex, setActiveGroupIndex] = useState<number>(defaultState.activeGroupIndex);
  const [activeChannelTabIndex, setActiveChannelTabIndex] = useState<number>(defaultState.activeChannelTabIndex);
  const [activeDeviceTabIndex, setActiveDeviceTabIndex] = useState<number>(0);
  const [readingChat, setReadingChat] = useState<boolean>(defaultState.readingChat);

  const toggleGridView = () => {
    setIsDashboardGridView(!isDashboardGridView);
    saveExalusDataStorage('isDashboardGridView', !isDashboardGridView);
  };

  const setActiveGroup = (activeGroupIndex: number) => {
    setActiveGroupIndex(activeGroupIndex);
    saveExalusDataStorage('activeGroupIndex', activeGroupIndex);
  };

  const setActiveChannelTab = (activeChannelTabIndex: number) => {
    setActiveChannelTabIndex(activeChannelTabIndex);
    saveExalusDataStorage('activeChannelTabIndex', activeChannelTabIndex);
  };

  const toggleChannelsListView = () => {
    setIsChannelsListView(!isChannelsListView);
    saveExalusDataStorage('isChannelsListView', !isChannelsListView);
  };

  const toggleReadingChat = () => {
    setReadingChat(!readingChat);
    saveExalusDataStorage('readingChat', !readingChat);
  };

  const getStorageData = () => {
    const dashboardGridView = readExalusDataStorage<boolean>('isDashboardGridView');
    if (dashboardGridView !== null) setIsDashboardGridView(dashboardGridView);

    const channelsListView = readExalusDataStorage<boolean>('isChannelsListView');
    if (channelsListView !== null) setIsChannelsListView(channelsListView);

    const groupIndex = readExalusDataStorage<number>('activeGroupIndex');
    if (groupIndex !== null) setActiveGroupIndex(groupIndex);

    const channelTabIndex = readExalusDataStorage<number>('activeChannelTabIndex');
    if (channelTabIndex !== null) setActiveChannelTabIndex(channelTabIndex);

    const chat = readExalusDataStorage<boolean>('readingChat');
    if (chat !== null) setReadingChat(chat);
  };

  const values = {
    isDashboardGridView,
    toggleGridView,
    activeGroupIndex,
    setActiveGroup,
    activeChannelTabIndex,
    setActiveChannelTab,
    activeDeviceTabIndex,
    setActiveDeviceTabIndex,
    isChannelsListView,
    toggleChannelsListView,
    readingChat,
    toggleReadingChat,
    getStorageData,
  };

  return <ExalusStorageContext.Provider value={{ ...values }}>{children}</ExalusStorageContext.Provider>;
};
